import React, { useEffect, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Assignment as ReviewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DriveFileMove as DriveFileMoveIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Undo as ReturnIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  FiberManualRecord as StatusDotIcon
} from '@mui/icons-material';
import type { LessonPlan } from '../types';
import { LESSON_PLAN_STATUSES } from '../types';
import { lessonPlanService } from '../services/lessonPlanService';
import { useAppStore } from '../stores/useAppStore';
import { isAdminOrApprover } from '../utils/rbac';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { LessonPlanFileViewer } from './LessonPlanFileViewer';
import { logger } from '../utils/logger';
import { FolderSelector } from './FolderSelector';
import { lessonPlanFolderService } from '../services/lessonPlanFolderService';
import type { LessonPlanFolder } from '../types';


interface LessonPlanListProps {
  lessonPlans: LessonPlan[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRefresh?: () => void;
  onDeleteLessonPlan?: (id: string) => void;
  onViewLessonPlan?: (id: string) => void;
  onDownloadLessonPlan?: (id: string) => void;
  onReviseLessonPlan?: (id: string) => void;
  onEditLessonPlan?: (lessonPlan: LessonPlan) => void;
  showMyPlans?: boolean;
  showAdminActions?: boolean;
  onSelectedIdsChange?: (ids: string[]) => void;
  onBulkDeleteClick?: () => void;
  onBulkMoveClick?: () => void;
  selectedLessonPlanIds?: string[];
  folders?: LessonPlanFolder[];
  showStatusLegend?: boolean;
}

export const LessonPlanList: React.FC<LessonPlanListProps> = ({
  lessonPlans: propsLessonPlans,
  loading,
  error,
  page,
  totalPages,
  totalCount,
  onPageChange,
  onRefresh,
  onDeleteLessonPlan,
  onViewLessonPlan,
  onDownloadLessonPlan,
  onReviseLessonPlan,
  onEditLessonPlan,
  showMyPlans = false,
  showAdminActions = false,
  onSelectedIdsChange,
  onBulkDeleteClick,
  onBulkMoveClick,
  selectedLessonPlanIds: externalSelectedIds,
  folders = [],
  showStatusLegend = true
}) => {

  const [selectedLessonPlan, setSelectedLessonPlan] = useState<LessonPlan | null>(null);
  const [selectedLessonPlanWithHistory, setSelectedLessonPlanWithHistory] = useState<LessonPlan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
  const [reviseFile, setReviseFile] = useState<File | null>(null);
  const [reviseNotes, setReviseNotes] = useState('');
  const [isRevising, setIsRevising] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [bulkRevisionNotes, setBulkRevisionNotes] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [lessonPlanForViewer, setLessonPlanForViewer] = useState<LessonPlan | null>(null);
  const [jumpPage, setJumpPage] = useState<string>('');

  // Defensive check for props
  const lessonPlans = Array.isArray(propsLessonPlans) ? propsLessonPlans : [];

  // Use external selectedIds if provided, otherwise use internal state
  const selectedLessonPlanIds = externalSelectedIds !== undefined ? externalSelectedIds : internalSelectedIds;
  const setSelectedLessonPlanIds = (ids: string[] | ((prev: string[]) => string[])) => {
    const newIds = typeof ids === 'function' ? ids(selectedLessonPlanIds) : ids;
    if (externalSelectedIds !== undefined) {
      onSelectedIdsChange?.(newIds);
    } else {
      setInternalSelectedIds(newIds);
    }
  };

  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const isPrivilegedUser = isAdminOrApprover(userRoles);
  // Show checkbox if: (1) Admin/Approver with admin actions, OR (2) Selection is enabled via onSelectedIdsChange
  const canBulkDelete = (isAdminOrApprover(userRoles) && showAdminActions) || (onSelectedIdsChange !== undefined);

  // If user is NOT Admin/BGH, they cannot edit/delete/move "Approved" lesson plans
  useEffect(() => {
    if (isPrivilegedUser) return;
    // Remove any selected ids that are Approved (safety cleanup)
    setSelectedLessonPlanIds((prev) => {
      const allowedIds = new Set(lessonPlans.filter((lp) => lp.status !== 'Approved').map((lp) => lp.id));
      return prev.filter((id) => allowedIds.has(id));
    });
  }, [isPrivilegedUser, lessonPlans]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getStatusMeta = useCallback((status: string): { label: string; color: string } => {
    // Colors per request:
    // - Pending: xanh dương
    // - UnderReview: vàng
    // - Approved: xanh lá
    // - NeedsRevision: cam
    switch (status) {
      case 'Pending':
        return { label: 'Chờ duyệt', color: '#1976d2' };
      case 'PendingSubmit':
        return { label: 'Chờ nộp', color: '#6a1b9a' };
      case 'UnderReview':
        return { label: 'Đang xem xét', color: '#f9a825' };
      case 'Approved':
        return { label: 'Đã duyệt', color: '#2e7d32' };
      case 'NeedsRevision':
        return { label: 'Cần chỉnh sửa', color: '#fb8c00' };
      case 'Draft':
        return { label: 'Bản nháp', color: '#9e9e9e' };
      default: {
        const statusConfig = LESSON_PLAN_STATUSES.find(s => s.value === status);
        return { label: statusConfig?.label || status, color: '#9e9e9e' };
      }
    }
  }, []);

  const handleViewLessonPlan = async (lessonPlan: LessonPlan) => {
    // For non-digital plans, verify file actually exists on server
    if (!lessonPlan.is_digital) {
      const fileExists = await lessonPlanService.checkFileExists(lessonPlan);
      if (!fileExists) {
        showError('Giáo án này không có file đính kèm');
        return;
      }
    }

    // Fetch full lesson plan details (including content for digital plans)
    try {
      const fullLessonPlan = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      setLessonPlanForViewer(fullLessonPlan);
      setFileViewerOpen(true);
    } catch (error) {
      logger.error('Error fetching lesson plan details:', error);
      showError('Không thể tải thông tin giáo án');
    }
  };

  const handleViewDetail = (lessonPlan: LessonPlan) => {
    // Open detail dialog (for icon mắt)
    onViewLessonPlan?.(lessonPlan.id);
  };

  const handleSubmitDraft = useCallback(async (lessonPlan: LessonPlan) => {
    try {
      const result = await lessonPlanService.submitDraftLessonPlan(lessonPlan.id);
      if (result.status === 'PendingSubmit') {
        showSuccess(result.message || 'Đã chuyển sang trạng thái "Chờ nộp". Hệ thống sẽ tự nộp khi tới thời gian nộp giáo án.');
      } else {
        showSuccess('Đã nộp giáo án thành công!');
      }
      onRefresh?.();
    } catch (error) {
      logger.error('Error submitting draft lesson plan:', error);
      let errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi nộp giáo án';

      // Parse error response if it contains period info
      try {
        if (error instanceof Error && errorMessage.includes('Chưa đến hoặc đã hết thời gian')) {
          // Try to extract period from error message or parse JSON
          const errorText = errorMessage;
          if (errorText.includes('period') || errorText.includes('startDate')) {
            // Error already contains formatted message from backend
            errorMessage = errorText;
          } else {
            // Try to parse as JSON to get period
            const jsonMatch = errorText.match(/\{.*\}/);
            if (jsonMatch) {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData.period) {
                const startDate = new Date(errorData.period.startDate);
                const endDate = new Date(errorData.period.endDate);
                const startStr = startDate.toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                const endStr = endDate.toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                errorMessage = `Chưa đến hoặc đã hết thời gian nộp giáo án. Vui lòng liên hệ người phụ trách.\n\nThời gian nộp giáo án:\nTừ: ${startStr}\nĐến: ${endStr}`;
              }
            }
          }
        }
      } catch (parseErr) {
        // If parsing fails, use original error message
        logger.warn('Error parsing error message:', parseErr);
      }

      showError(errorMessage);
    }
  }, [showSuccess, showError, onRefresh]);

  const handleDownloadLessonPlan = useCallback(async (lessonPlan: LessonPlan) => {
    try {
      // For digital plans, fetch full details and generate PDF
      if (lessonPlan.is_digital) {
        // Fetch full lesson plan to get content
        const fullLessonPlan = await lessonPlanService.getLessonPlanById(lessonPlan.id);
        const content = fullLessonPlan.content || '<p>Không có nội dung</p>';

        // Import html2pdf dynamically
        const html2pdf = (await import('html2pdf.js')).default;

        // Create a temporary container for PDF generation
        const element = document.createElement('div');
        element.innerHTML = `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h1 style="text-align: center; color: #333; margin-bottom: 20px;">${fullLessonPlan.title}</h1>
            <div style="margin-bottom: 30px;">
              <p><strong>Giáo viên:</strong> ${fullLessonPlan.submittedByUser?.fullName || 'N/A'}</p>
              <p><strong>Môn học:</strong> ${fullLessonPlan.subject || 'N/A'}</p>
              <p><strong>Tuần:</strong> ${fullLessonPlan.week || 'N/A'}</p>
            </div>
            <hr style="border: 1px solid #eee; margin-bottom: 30px;" />
            <div class="content">
              ${content}
            </div>
          </div>
        `;

        const opt = {
          margin: 10,
          filename: `${fullLessonPlan.title}.pdf`,
          image: { type: 'jpeg' as const, quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
        };

        // Generate and save
        html2pdf().set(opt).from(element).save();
        // Note: Don't call onDownloadLessonPlan - we already handled the download
        return;
      }

      // For file-based plans, check if file exists first
      const fileExists = await lessonPlanService.checkFileExists(lessonPlan);
      if (!fileExists) {
        showError('Giáo án này không có file đính kèm');
        return;
      }

      // For file-based plans, use the normal download
      await lessonPlanService.downloadLessonPlanFileDirectly(lessonPlan);
      // Note: Don't call onDownloadLessonPlan - we already handled the download
    } catch (error) {
      logger.error('Error downloading lesson plan:', error);
      showError('Không thể tải file giáo án');
    }
  }, [showError]);

  const handleDeleteClick = useCallback((lessonPlan: LessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setDeleteDialogOpen(true);
  }, []);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (isPrivilegedUser) {
        setSelectedLessonPlanIds(lessonPlans.map(lp => lp.id));
      } else {
        // Non-privileged users can bulk select everything except Approved
        setSelectedLessonPlanIds(lessonPlans.filter(lp => lp.status !== 'Approved').map(lp => lp.id));
      }
    } else {
      setSelectedLessonPlanIds([]);
    }
  };

  const handleSelectLessonPlan = (lessonPlanId: string) => {
    if (!isPrivilegedUser) {
      const lp = lessonPlans.find((x) => x.id === lessonPlanId);
      if (!lp || lp.status === 'Approved') return;
    }
    setSelectedLessonPlanIds(prev => {
      if (prev.includes(lessonPlanId)) {
        return prev.filter(id => id !== lessonPlanId);
      } else {
        return [...prev, lessonPlanId];
      }
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // If onBulkDeleteClick is provided (from parent), use it; otherwise show dialog
    if (onBulkDeleteClick) {
      onBulkDeleteClick();
    } else {
      setBulkDeleteDialogOpen(true);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const response = await lessonPlanService.bulkDeleteLessonPlans(selectedLessonPlanIds);

      if (response.success) {
        showSuccess(`Đã xóa ${response.deletedCount} giáo án thành công`);
        // Use flushSync to force synchronous state update and close dialog immediately
        flushSync(() => {
          setBulkDeleteDialogOpen(false);
          setSelectedLessonPlanIds([]);
        });
        // Small delay to ensure DB updates before refresh
        setTimeout(() => {
          onRefresh?.();
        }, 500);
      } else {
        showError(response.message || 'Không thể xóa giáo án');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể xóa giáo án');
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleBulkMoveClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // If onBulkMoveClick is provided (from parent), use it; otherwise show dialog
    if (onBulkMoveClick) {
      onBulkMoveClick();
    } else {
      setBulkMoveDialogOpen(true);
      setSelectedFolderId(null);
    }
  };

  const handleBulkMoveConfirm = async () => {
    if (selectedLessonPlanIds.length === 0) return;

    try {
      setIsMoving(true);
      const response = await lessonPlanFolderService.bulkMoveLessonPlansToFolder(
        selectedLessonPlanIds,
        selectedFolderId
      );

      if (response.success) {
        showSuccess(`Đã di chuyển ${response.movedCount} giáo án vào folder thành công!`);
        setBulkMoveDialogOpen(false);
        setSelectedFolderId(null);
        setSelectedLessonPlanIds([]);
        onRefresh?.();
      }
    } catch (err: any) {
      logger.error('Error moving lesson plans to folder:', err);
      const errorMessage = err?.response?.data?.error || 'Không thể di chuyển giáo án. Vui lòng thử lại sau.';
      showError(errorMessage);
    } finally {
      setIsMoving(false);
    }
  };

  const handleBulkMoveCancel = () => {
    setBulkMoveDialogOpen(false);
    setSelectedFolderId(null);
  };

  const handleBulkApproveClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // Filter to only allow approving lesson plans with status Pending or UnderReview
    const approvableIds = lessonPlans
      .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
      .map(lp => lp.id);

    if (approvableIds.length === 0) {
      showError('Không có giáo án nào có thể duyệt. Chỉ có thể duyệt giáo án ở trạng thái "Chờ duyệt" hoặc "Đang xem xét".');
      return;
    }

    setBulkApproveDialogOpen(true);
    setBulkApprovalNotes('');
  };

  const handleBulkApproveConfirm = async () => {
    if (selectedLessonPlanIds.length === 0) return;

    try {
      setIsBulkProcessing(true);
      const approvableIds = lessonPlans
        .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
        .map(lp => lp.id);

      if (approvableIds.length === 0) {
        showError('Không có giáo án nào có thể duyệt.');
        return;
      }

      const response = await lessonPlanService.bulkApproveLessonPlans(approvableIds, bulkApprovalNotes.trim() || undefined);

      if (response.success) {
        showSuccess(response.message);
        setBulkApproveDialogOpen(false);
        setBulkApprovalNotes('');
        setSelectedLessonPlanIds([]);
        onRefresh?.();
      } else {
        showError(response.message || 'Không thể duyệt giáo án');
        if (response.errors && response.errors.length > 0) {
          logger.warn('Bulk approve errors:', response.errors);
        }
      }
    } catch (err) {
      logger.error('Error bulk approving lesson plans:', err);
      showError(err instanceof Error ? err.message : 'Không thể duyệt giáo án');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkApproveCancel = () => {
    setBulkApproveDialogOpen(false);
    setBulkApprovalNotes('');
  };

  const handleBulkRejectClick = () => {
    if (selectedLessonPlanIds.length === 0) return;
    // Filter to only allow rejecting lesson plans with status Pending or UnderReview
    const rejectableIds = lessonPlans
      .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
      .map(lp => lp.id);

    if (rejectableIds.length === 0) {
      showError('Không có giáo án nào có thể yêu cầu chỉnh sửa. Chỉ có thể yêu cầu chỉnh sửa giáo án ở trạng thái "Chờ duyệt" hoặc "Đang xem xét".');
      return;
    }

    setBulkRejectDialogOpen(true);
    setBulkRevisionNotes('');
  };

  const handleBulkRejectConfirm = async () => {
    if (selectedLessonPlanIds.length === 0 || !bulkRevisionNotes.trim()) {
      showError('Vui lòng nhập góp ý chỉnh sửa');
      return;
    }

    try {
      setIsBulkProcessing(true);
      const rejectableIds = lessonPlans
        .filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview'))
        .map(lp => lp.id);

      if (rejectableIds.length === 0) {
        showError('Không có giáo án nào có thể yêu cầu chỉnh sửa.');
        return;
      }

      const response = await lessonPlanService.bulkRejectLessonPlans(rejectableIds, bulkRevisionNotes.trim());

      if (response.success) {
        showSuccess(response.message);
        setBulkRejectDialogOpen(false);
        setBulkRevisionNotes('');
        setSelectedLessonPlanIds([]);
        onRefresh?.();
      } else {
        showError(response.message || 'Không thể yêu cầu chỉnh sửa giáo án');
        if (response.errors && response.errors.length > 0) {
          logger.warn('Bulk reject errors:', response.errors);
        }
      }
    } catch (err) {
      logger.error('Error bulk rejecting lesson plans:', err);
      showError(err instanceof Error ? err.message : 'Không thể yêu cầu chỉnh sửa giáo án');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkRejectCancel = () => {
    setBulkRejectDialogOpen(false);
    setBulkRevisionNotes('');
  };

  const handleCopyLink = async (lessonPlan: LessonPlan) => {
    try {
      // Create shareable public link - URL to public lesson plan page
      const shareableLink = `${window.location.origin}/public/lesson-plan/${lessonPlan.id}`;

      // Try modern Clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareableLink);
          showSuccess('Đã sao chép link!');
          return;
        } catch (clipboardError) {
          logger.warn('Clipboard API failed, trying fallback:', clipboardError);
          // Fall through to fallback method
        }
      }

      // Fallback method for environments without Clipboard API (HTTP, older browsers)
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showSuccess('Đã sao chép link!');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        logger.error('Fallback copy method failed:', fallbackError);
        // Last resort: show the link in a prompt
        window.prompt('Không thể tự động sao chép. Vui lòng sao chép link sau:', shareableLink);
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err: any) {
      logger.error('Error copying link:', err);
      showError('Không thể sao chép link. Vui lòng thử lại.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLessonPlan) return;

    const lessonPlanId = selectedLessonPlan.id;
    setDeleteDialogOpen(false);
    setSelectedLessonPlan(null);

    // Let parent component handle the deletion (including error handling and refresh)
    onDeleteLessonPlan?.(lessonPlanId);
  };

  const handleReviseClick = useCallback((lessonPlan: LessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setReviseDialogOpen(true);
  }, []);

  const handleReviseSubmit = async () => {
    if (!selectedLessonPlan || !reviseFile) return;

    setIsRevising(true);
    try {
      const formData = {
        file: reviseFile,
        revisionNotes: reviseNotes.trim() || undefined
      };

      const updatedLessonPlan = await lessonPlanService.reviseLessonPlan(selectedLessonPlan.id, formData);

      logger.log('📋 Revised lesson plan response:', updatedLessonPlan);
      logger.log('📋 Revised lesson plan - fileName:', updatedLessonPlan.fileName);
      logger.log('📋 Revised lesson plan - filePath:', updatedLessonPlan.filePath);
      const revHistory = (updatedLessonPlan as any)?.revisionHistory;
      logger.log('📋 Revised lesson plan - revisionHistory:', revHistory);
      if (revHistory && Array.isArray(revHistory) && revHistory.length > 0) {
        const lastEntry = revHistory[revHistory.length - 1];
        logger.log('📋 Revised lesson plan - Last history entry:', lastEntry);
        logger.log('📋 Revised lesson plan - Last entry action:', lastEntry.action);
        logger.log('📋 Revised lesson plan - Last entry newFileName:', lastEntry.newFileName);
        logger.log('📋 Revised lesson plan - Last entry newFilePath:', lastEntry.newFilePath);
        logger.log('📋 Revised lesson plan - Last entry oldFileName:', lastEntry.oldFileName);
        logger.log('📋 Revised lesson plan - Last entry oldFilePath:', lastEntry.oldFilePath);
      }

      // Show success message
      showSuccess('Nộp lại giáo án thành công!');

      // Update selectedLessonPlan with the new data from server
      // This ensures the detail dialog (if open) shows the updated file info
      // Use updatedLessonPlan from API response which has the latest data
      setSelectedLessonPlan(updatedLessonPlan);

      // Close dialog first
      setReviseDialogOpen(false);
      setReviseFile(null);
      setReviseNotes('');

      // Refresh the list IMMEDIATELY to show updated lesson plan (including new filename)
      // This ensures list items show the new filename right away
      // Force immediate refresh to update list items
      onRefresh?.();

      // Also refresh again after a short delay to ensure DB is fully updated
      // Use setTimeout to ensure dialog is closed before refreshing
      // Multiple refreshes to ensure data is updated
      setTimeout(() => {
        onRefresh?.();
      }, 300);

      // Final refresh after longer delay to ensure all updates are complete
      setTimeout(() => {
        onRefresh?.();
      }, 800);
    } catch (error) {
      logger.error('Error revising lesson plan:', error);

      // Extract error message from error
      let errorMessage = 'Có lỗi xảy ra khi nộp lại giáo án';

      if (error instanceof Error) {
        const errorText = error.message;

        // Check if it's a 401 Unauthorized error
        if (errorText.includes('401') || errorText.includes('Unauthorized') || errorText.includes('Authentication required')) {
          errorMessage = 'Bạn không có quyền thực hiện thao tác này. Vui lòng đăng nhập lại hoặc liên hệ quản trị viên.';
        } else if (errorText.includes('403') || errorText.includes('Forbidden')) {
          errorMessage = 'Bạn không có quyền nộp lại giáo án này.';
        } else if (errorText.includes('404') || errorText.includes('not found')) {
          errorMessage = 'Không tìm thấy giáo án. Vui lòng thử lại sau.';
        } else if (errorText.includes('400') || errorText.includes('Bad Request')) {
          // Try to extract message from error response
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            errorMessage = 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại file và thông tin.';
          }
        } else if (errorText.includes('500') || errorText.includes('Internal Server Error')) {
          errorMessage = 'Lỗi máy chủ. Vui lòng thử lại sau.';
        } else if (errorText.trim()) {
          // Try to extract message from error response JSON
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            // If not JSON, use the error message as is (but limit length)
            errorMessage = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText;
          }
        }
      }

      showError(errorMessage);
    } finally {
      setIsRevising(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = lessonPlanService.validateFile(file);
      if (validation.isValid) {
        setReviseFile(file);
      } else {
        alert(validation.error);
      }
    }
  };

  // Admin functions
  const handleApproveClick = async (lessonPlan: LessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setApproveDialogOpen(true);
    // Load full lesson plan details including revision history
    try {
      const details = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      setSelectedLessonPlanWithHistory(details);
    } catch (error) {
      logger.error('Error loading lesson plan details:', error);
      setSelectedLessonPlanWithHistory(lessonPlan);
    }
  };

  const handleApproveConfirm = async () => {
    if (!selectedLessonPlan) return;

    try {
      await lessonPlanService.approveLessonPlan(selectedLessonPlan.id, approvalNotes.trim() || undefined);
      showSuccess('Duyệt giáo án thành công!');
      onRefresh?.();
    } catch (error) {
      logger.error('Error approving lesson plan:', error);
      showError('Có lỗi xảy ra khi duyệt giáo án');
    } finally {
      setApproveDialogOpen(false);
      setApprovalNotes('');
      setSelectedLessonPlan(null);
      setSelectedLessonPlanWithHistory(null);
    }
  };

  const handleReviewClick = useCallback(async (lessonPlan: LessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setReviewDialogOpen(true);
    // Load full lesson plan details including revision history
    try {
      const details = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      setSelectedLessonPlanWithHistory(details);
    } catch (error) {
      logger.error('Error loading lesson plan details:', error);
      setSelectedLessonPlanWithHistory(lessonPlan);
    }
  }, []);

  const handleReviewSubmit = async () => {
    if (!selectedLessonPlan) return;

    try {
      await lessonPlanService.reviewLessonPlan(selectedLessonPlan.id, reviewNotes.trim() || undefined);
      showSuccess('Giáo án đã được chuyển sang trạng thái Đang xem xét!');
      onRefresh?.();
    } catch (error) {
      logger.error('Error reviewing lesson plan:', error);
      showError('Có lỗi xảy ra khi chuyển trạng thái giáo án');
    } finally {
      setReviewDialogOpen(false);
      setReviewNotes('');
      setSelectedLessonPlan(null);
      setSelectedLessonPlanWithHistory(null);
    }
  };

  const handleRejectClick = useCallback(async (lessonPlan: LessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setRevisionDialogOpen(true);
    // Load full lesson plan details including revision history
    try {
      const details = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      setSelectedLessonPlanWithHistory(details);
    } catch (error) {
      logger.error('Error loading lesson plan details:', error);
      setSelectedLessonPlanWithHistory(lessonPlan);
    }
  }, []);

  const handleRevisionSubmit = async () => {
    if (!selectedLessonPlan || !revisionNotes.trim()) return;

    try {
      await lessonPlanService.rejectLessonPlan(selectedLessonPlan.id, revisionNotes);
      showSuccess('Yêu cầu chỉnh sửa đã được gửi thành công!');
      onRefresh?.();
    } catch (error) {
      logger.error('Error rejecting lesson plan:', error);
      showError('Có lỗi xảy ra khi gửi yêu cầu chỉnh sửa');
    } finally {
      setRevisionDialogOpen(false);
      setRevisionNotes('');
      setSelectedLessonPlan(null);
      setSelectedLessonPlanWithHistory(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!lessonPlans || lessonPlans.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {showMyPlans ? 'Bạn chưa nộp giáo án nào' : 'Không có giáo án nào'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {showMyPlans ? 'Hãy nộp giáo án đầu tiên của bạn' : 'Hãy thử thay đổi bộ lọc hoặc tìm kiếm với từ khóa khác'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Bulk actions buttons - chỉ hiển thị khi không có onBulkDeleteClick/onBulkMoveClick từ parent (parent sẽ tự xử lý) */}
      {selectedLessonPlanIds.length > 0 && (!onBulkDeleteClick || !onBulkMoveClick) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          {/* Bulk move button - chỉ hiển thị khi không có onBulkMoveClick từ parent */}
          {!onBulkMoveClick && (
            <Tooltip title={`Di chuyển (${selectedLessonPlanIds.length})`} arrow>
              <IconButton
                color="primary"
                onClick={handleBulkMoveClick}
                aria-label="Di chuyển"
                size="small"
                disabled={isBulkProcessing}
              >
                <DriveFileMoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Bulk delete button - chỉ hiển thị khi không có onBulkDeleteClick từ parent */}
          {canBulkDelete && !onBulkDeleteClick && (
            <Tooltip title={`Xóa (${selectedLessonPlanIds.length})`} arrow>
              <IconButton
                color="error"
                onClick={handleBulkDeleteClick}
                aria-label="Xóa"
                size="small"
                disabled={isBulkProcessing}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {showStatusLegend && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {(['Pending', 'UnderReview', 'Approved', 'NeedsRevision'] as const).map((s) => {
              const meta = getStatusMeta(s);
              return (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StatusDotIcon sx={{ fontSize: 12, color: meta.color }} />
                  <Typography variant="caption" color="text.secondary">
                    {meta.label}
                  </Typography>
                </Box>
              );
            })}
            {(showMyPlans || lessonPlans.some(lp => lp.status === 'Draft')) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StatusDotIcon sx={{ fontSize: 12, color: getStatusMeta('Draft').color }} />
                <Typography variant="caption" color="text.secondary">
                  {getStatusMeta('Draft').label}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: canBulkDelete ? '50%' : '55%' }}>Tiêu đề</TableCell>
              <TableCell align="center" sx={{ width: canBulkDelete ? '40%' : '38%' }}>Thao tác</TableCell>
              {canBulkDelete && (
                <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                  <Checkbox
                    indeterminate={selectedLessonPlanIds.length > 0 && selectedLessonPlanIds.length < lessonPlans.length}
                    checked={lessonPlans.length > 0 && selectedLessonPlanIds.length === lessonPlans.length}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {lessonPlans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canBulkDelete ? 3 : 2} align="center" sx={{ py: 8 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h6" color="text.secondary">
                      {showMyPlans ? 'Bạn chưa nộp giáo án nào' : 'Không có giáo án nào'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {showMyPlans ? 'Hãy nộp giáo án đầu tiên của bạn' : 'Hãy thử thay đổi bộ lọc hoặc tìm kiếm với từ khóa khác'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              lessonPlans.map((lessonPlan) => {
                const isSelected = selectedLessonPlanIds.includes(lessonPlan.id);
                const statusMeta = getStatusMeta(lessonPlan.status);

                return (
                  <TableRow
                    key={lessonPlan.id}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      ...(lessonPlan.revisionNotes ? {
                        backgroundColor: 'warning.lighter'
                      } : {})
                    }}
                  >
                    <TableCell sx={{ width: canBulkDelete ? '50%' : '55%', maxWidth: canBulkDelete ? '50%' : '55%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Tooltip title={statusMeta.label} arrow>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <StatusDotIcon sx={{ fontSize: 12, color: statusMeta.color }} />
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography variant="body2" fontWeight="bold">Tên: {lessonPlan.title}</Typography>
                              <Typography variant="caption" display="block">Người nộp: {lessonPlan.submittedByUser?.fullName || '-'}</Typography>
                              <Typography variant="caption" display="block">Ngày nộp: {formatDate(lessonPlan.submissionDate)}</Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleViewLessonPlan(lessonPlan)}
                          >
                            {lessonPlan.title}
                          </Typography>
                        </Tooltip>
                      </Box>

                      {lessonPlan.submittedByUser && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          👤 {lessonPlan.submittedByUser?.fullName || '-'}
                        </Typography>
                      )}
                      {lessonPlan.revisionNotes && lessonPlan.status !== 'Approved' && (
                        <Tooltip title={lessonPlan.revisionNotes} arrow>
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            ⚠️ {lessonPlan.revisionNotes}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Xem chi tiết">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetail(lessonPlan)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Tải xuống">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadLessonPlan(lessonPlan)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {showAdminActions && lessonPlan.status === 'Pending' && (
                          <Tooltip title="Đang xem xét">
                            <IconButton
                              size="small"
                              onClick={() => handleReviewClick(lessonPlan)}
                              color="info"
                            >
                              <ReviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showAdminActions && (lessonPlan.status === 'Pending' || lessonPlan.status === 'UnderReview') && (
                          <>
                            <Tooltip title="Duyệt">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveClick(lessonPlan)}
                                color="success"
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Yêu cầu chỉnh sửa">
                              <IconButton
                                size="small"
                                onClick={() => handleRejectClick(lessonPlan)}
                                color="error"
                              >
                                <ReturnIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {showMyPlans && lessonPlan.status === 'Draft' && (
                          <Tooltip title="Nộp giáo án">
                            <IconButton
                              size="small"
                              onClick={() => handleSubmitDraft(lessonPlan)}
                              color="primary"
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showMyPlans && lessonPlan.status === 'NeedsRevision' && (
                          <Tooltip title="Nộp lại">
                            <IconButton
                              size="small"
                              onClick={() => handleReviseClick(lessonPlan)}
                              color="primary"
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showMyPlans && (
                          <>
                            {onEditLessonPlan && (isPrivilegedUser || lessonPlan.status !== 'Approved') && (
                              <Tooltip title="Chỉnh sửa">
                                <IconButton
                                  size="small"
                                  onClick={() => onEditLessonPlan(lessonPlan)}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Chia sẻ">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(lessonPlan)}
                                color="primary"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(isPrivilegedUser || lessonPlan.status !== 'Approved') && (
                              <Tooltip title="Xóa">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(lessonPlan)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                    {canBulkDelete && (
                      <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectLessonPlan(lessonPlan.id)}
                          disabled={!isPrivilegedUser && lessonPlan.status === 'Approved'}
                          size="small"
                          sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2, flexWrap: 'wrap' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Đến trang:</Typography>
            <TextField
              size="small"
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const p = parseInt(jumpPage);
                  if (!isNaN(p) && p >= 1 && p <= totalPages) {
                    onPageChange(p);
                    setJumpPage('');
                  }
                }
              }}
              sx={{ width: 70 }}
              inputProps={{
                style: { textAlign: 'center', padding: '4px 8px' },
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const p = parseInt(jumpPage);
                if (!isNaN(p) && p >= 1 && p <= totalPages) {
                  onPageChange(p);
                  setJumpPage('');
                }
              }}
              sx={{ minWidth: 40, p: '4px 8px' }}
            >
              Đi
            </Button>
          </Box>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa giáo án "{selectedLessonPlan?.title}"?
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revise Dialog */}
      <Dialog open={reviseDialogOpen} onClose={() => setReviseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nộp lại giáo án</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Giáo án: {selectedLessonPlan?.title}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="revise-file-input"
            />
            <label htmlFor="revise-file-input">
              <Button variant="outlined" component="span" fullWidth>
                {reviseFile ? reviseFile.name : 'Chọn file mới'}
              </Button>
            </label>
          </Box>

          <TextField
            fullWidth
            label="Ghi chú về việc chỉnh sửa"
            multiline
            rows={3}
            value={reviseNotes}
            onChange={(e) => setReviseNotes(e.target.value)}
            placeholder="Mô tả những thay đổi đã thực hiện..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviseDialogOpen(false)}>Hủy</Button>
          <Button
            onClick={handleReviseSubmit}
            variant="contained"
            disabled={!reviseFile || isRevising}
          >
            {isRevising ? 'Đang xử lý...' : 'Nộp lại'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Duyệt giáo án</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedLessonPlan?.title}
          </Typography>

          <TextField
            fullWidth
            label="Ghi chú nhận xét"
            multiline
            rows={4}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Nhập nhận xét về giáo án (tùy chọn)..."
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Lịch sử nhận xét
          </Typography>

          {(selectedLessonPlanWithHistory as any)?.revisionHistory && Array.isArray((selectedLessonPlanWithHistory as any).revisionHistory) && (selectedLessonPlanWithHistory as any).revisionHistory.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {(selectedLessonPlanWithHistory as any).revisionHistory.map((history: any, index: number) => {
                const getActionLabel = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'Nộp giáo án';
                    case 'reviewed':
                      return 'Đang xem xét';
                    case 'rejected':
                      return 'Yêu cầu chỉnh sửa';
                    case 'revised':
                      return 'Nộp lại';
                    case 'approved':
                      return 'Đã duyệt';
                    default:
                      return 'Cập nhật';
                  }
                };

                const getActionColor = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'primary';
                    case 'reviewed':
                      return 'info';
                    case 'rejected':
                      return 'warning';
                    case 'revised':
                      return 'primary';
                    case 'approved':
                      return 'success';
                    default:
                      return 'grey';
                  }
                };

                const isLast = index === (selectedLessonPlanWithHistory as any).revisionHistory.length - 1;

                return (
                  <Box key={history.id || index} sx={{ position: 'relative', pl: 4, pb: isLast ? 0 : 3 }}>
                    {/* Timeline Line */}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 15,
                          top: 40,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'divider'
                        }}
                      />
                    )}

                    {/* Timeline Dot */}
                    <Avatar
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 4,
                        width: 32,
                        height: 32,
                        bgcolor: `${getActionColor(history.action)}.main`,
                        color: 'white'
                      }}
                    >
                      {history.action === 'submitted' ? 'N' :
                        history.action === 'reviewed' ? 'X' :
                          history.action === 'rejected' ? 'C' :
                            history.action === 'revised' ? 'L' :
                              history.action === 'approved' ? 'D' : '?'}
                    </Avatar>

                    {/* Timeline Content */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={`${getActionColor(history.action)}.main`}>
                          {getActionLabel(history.action)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {history.date ? new Date(history.date).toLocaleString('vi-VN') : 'Chưa có ngày'}
                        </Typography>
                      </Box>

                      {history.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', mb: 1 }}>
                          {history.notes}
                        </Typography>
                      )}

                      {history.action === 'revised' && history.newFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File mới:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              if (selectedLessonPlan) {
                                // Download current file (new file)
                                const downloadUrl = `/api/lesson-plans/${selectedLessonPlan.id}/download`;
                                window.open(downloadUrl, '_blank');
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.newFileName}
                          </Button>
                        </Box>
                      )}
                      {history.oldFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File cũ:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              if (history.oldFilePath && selectedLessonPlan) {
                                try {
                                  const blobUrl = await lessonPlanService.downloadLessonPlanFileWithHeaders(selectedLessonPlan.id, history.oldFilePath);
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.download = history.oldFileName || 'lesson-plan.pdf';
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(blobUrl);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  logger.error('Error downloading file:', err);
                                  alert('Không thể tải xuống file');
                                }
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.oldFileName}
                          </Button>
                        </Box>
                      )}

                      {history.user && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                          Người thực hiện: {history.user.fullName || history.user.name || 'Không xác định'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Chưa có lịch sử nhận xét
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setApproveDialogOpen(false);
            setApprovalNotes('');
          }}>
            Hủy
          </Button>
          <Button onClick={handleApproveConfirm} variant="contained" color="success">
            XÁC NHẬN DUYỆT
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog - Đang xem xét */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Đang xem xét giáo án</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedLessonPlan?.title}
          </Typography>

          <TextField
            fullWidth
            label="Ghi chú nhận xét"
            multiline
            rows={4}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Nhập nhận xét về giáo án (tùy chọn)..."
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Lịch sử nhận xét
          </Typography>

          {(selectedLessonPlanWithHistory as any)?.revisionHistory && Array.isArray((selectedLessonPlanWithHistory as any).revisionHistory) && (selectedLessonPlanWithHistory as any).revisionHistory.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {(selectedLessonPlanWithHistory as any).revisionHistory.map((history: any, index: number) => {
                const getActionLabel = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'Nộp giáo án';
                    case 'reviewed':
                      return 'Đang xem xét';
                    case 'rejected':
                      return 'Yêu cầu chỉnh sửa';
                    case 'revised':
                      return 'Nộp lại';
                    case 'approved':
                      return 'Đã duyệt';
                    default:
                      return 'Cập nhật';
                  }
                };

                const getActionColor = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'primary';
                    case 'reviewed':
                      return 'info';
                    case 'rejected':
                      return 'warning';
                    case 'revised':
                      return 'primary';
                    case 'approved':
                      return 'success';
                    default:
                      return 'grey';
                  }
                };

                const isLast = index === (selectedLessonPlanWithHistory as any).revisionHistory.length - 1;

                return (
                  <Box key={history.id || index} sx={{ position: 'relative', pl: 4, pb: isLast ? 0 : 3 }}>
                    {/* Timeline Line */}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 15,
                          top: 40,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'divider'
                        }}
                      />
                    )}

                    {/* Timeline Dot */}
                    <Avatar
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 4,
                        width: 32,
                        height: 32,
                        bgcolor: `${getActionColor(history.action)}.main`,
                        color: 'white'
                      }}
                    >
                      {history.action === 'submitted' ? 'N' :
                        history.action === 'reviewed' ? 'X' :
                          history.action === 'rejected' ? 'C' :
                            history.action === 'revised' ? 'L' :
                              history.action === 'approved' ? 'D' : '?'}
                    </Avatar>

                    {/* Timeline Content */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={`${getActionColor(history.action)}.main`}>
                          {getActionLabel(history.action)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {history.date ? new Date(history.date).toLocaleString('vi-VN') : 'Chưa có ngày'}
                        </Typography>
                      </Box>

                      {history.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', mb: 1 }}>
                          {history.notes}
                        </Typography>
                      )}

                      {history.action === 'revised' && history.newFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File mới:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              if (selectedLessonPlan) {
                                // Download current file (new file)
                                const downloadUrl = `/api/lesson-plans/${selectedLessonPlan.id}/download`;
                                window.open(downloadUrl, '_blank');
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.newFileName}
                          </Button>
                        </Box>
                      )}
                      {history.oldFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File cũ:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              if (history.oldFilePath && selectedLessonPlan) {
                                try {
                                  const blobUrl = await lessonPlanService.downloadLessonPlanFileWithHeaders(selectedLessonPlan.id, history.oldFilePath);
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.download = history.oldFileName || 'lesson-plan.pdf';
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(blobUrl);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  logger.error('Error downloading file:', err);
                                  alert('Không thể tải xuống file');
                                }
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.oldFileName}
                          </Button>
                        </Box>
                      )}

                      {history.user && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                          Người thực hiện: {history.user.fullName || history.user.name || 'Không xác định'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Chưa có lịch sử nhận xét
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Hủy
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color="info"
          >
            XÁC NHẬN XEM XÉT
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision Request Dialog */}
      <Dialog open={revisionDialogOpen} onClose={() => setRevisionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yêu cầu chỉnh sửa giáo án</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedLessonPlan?.title}
          </Typography>

          <TextField
            fullWidth
            label="Nội dung yêu cầu chỉnh sửa"
            multiline
            rows={4}
            value={revisionNotes}
            onChange={(e) => setRevisionNotes(e.target.value)}
            placeholder="Mô tả chi tiết những điểm cần chỉnh sửa..."
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Lịch sử nhận xét
          </Typography>

          {(selectedLessonPlanWithHistory as any)?.revisionHistory && Array.isArray((selectedLessonPlanWithHistory as any).revisionHistory) && (selectedLessonPlanWithHistory as any).revisionHistory.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {(selectedLessonPlanWithHistory as any).revisionHistory.map((history: any, index: number) => {
                const getActionLabel = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'Nộp giáo án';
                    case 'reviewed':
                      return 'Đang xem xét';
                    case 'rejected':
                      return 'Yêu cầu chỉnh sửa';
                    case 'revised':
                      return 'Nộp lại';
                    case 'approved':
                      return 'Đã duyệt';
                    default:
                      return 'Cập nhật';
                  }
                };

                const getActionColor = (action: string) => {
                  switch (action) {
                    case 'submitted':
                      return 'primary';
                    case 'reviewed':
                      return 'info';
                    case 'rejected':
                      return 'warning';
                    case 'revised':
                      return 'primary';
                    case 'approved':
                      return 'success';
                    default:
                      return 'grey';
                  }
                };

                const isLast = index === (selectedLessonPlanWithHistory as any).revisionHistory.length - 1;

                return (
                  <Box key={history.id || index} sx={{ position: 'relative', pl: 4, pb: isLast ? 0 : 3 }}>
                    {/* Timeline Line */}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 15,
                          top: 40,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'divider'
                        }}
                      />
                    )}

                    {/* Timeline Dot */}
                    <Avatar
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 4,
                        width: 32,
                        height: 32,
                        bgcolor: `${getActionColor(history.action)}.main`,
                        color: 'white'
                      }}
                    >
                      {history.action === 'submitted' ? 'N' :
                        history.action === 'reviewed' ? 'X' :
                          history.action === 'rejected' ? 'C' :
                            history.action === 'revised' ? 'L' :
                              history.action === 'approved' ? 'D' : '?'}
                    </Avatar>

                    {/* Timeline Content */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={`${getActionColor(history.action)}.main`}>
                          {getActionLabel(history.action)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {history.date ? new Date(history.date).toLocaleString('vi-VN') : 'Chưa có ngày'}
                        </Typography>
                      </Box>

                      {history.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', mb: 1 }}>
                          {history.notes}
                        </Typography>
                      )}

                      {history.action === 'revised' && history.newFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File mới:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              if (selectedLessonPlan) {
                                try {
                                  const blob = await lessonPlanService.downloadLessonPlan(selectedLessonPlan.id);
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = history.newFileName || 'lesson-plan.pdf';
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  logger.error('Error downloading file:', err);
                                  alert('Không thể tải xuống file');
                                }
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.newFileName}
                          </Button>
                        </Box>
                      )}
                      {history.oldFileName && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>File cũ:</strong>
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              if (history.oldFilePath && selectedLessonPlan) {
                                try {
                                  const blobUrl = await lessonPlanService.downloadLessonPlanFileWithHeaders(selectedLessonPlan.id, history.oldFilePath);
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.download = history.oldFileName || 'lesson-plan.pdf';
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(blobUrl);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  logger.error('Error downloading file:', err);
                                  alert('Không thể tải xuống file');
                                }
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            {history.oldFileName}
                          </Button>
                        </Box>
                      )}

                      {history.user && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                          Người thực hiện: {history.user.fullName || history.user.name || 'Không xác định'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Chưa có lịch sử nhận xét
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Hủy</Button>
          <Button
            onClick={handleRevisionSubmit}
            variant="contained"
            color="error"
            disabled={!revisionNotes.trim()}
          >
            Gửi yêu cầu chỉnh sửa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Move Dialog */}
      <Dialog open={bulkMoveDialogOpen} onClose={handleBulkMoveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Di chuyển {selectedLessonPlanIds.length} giáo án vào folder</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FolderSelector
              folders={folders}
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              label="Chọn folder đích"
              allowNone={true}
              noneLabel="Không có folder (gỡ khỏi folder)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkMoveCancel} disabled={isMoving}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkMoveConfirm}
            variant="contained"
            disabled={isMoving}
            startIcon={isMoving ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {isMoving ? 'Đang di chuyển...' : 'Di chuyển'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa {selectedLessonPlanIds.length} giáo án không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Hành động này không thể hoàn tác. Tất cả dữ liệu và file liên quan đến các giáo án này sẽ bị xóa vĩnh viễn.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog open={bulkApproveDialogOpen} onClose={handleBulkApproveCancel} maxWidth="md" fullWidth>
        <DialogTitle>Duyệt nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bạn đang duyệt {lessonPlans.filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview')).length} giáo án
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Ghi chú duyệt (tùy chọn)"
            value={bulkApprovalNotes}
            onChange={(e) => setBulkApprovalNotes(e.target.value)}
            placeholder="Nhập ghi chú duyệt cho tất cả giáo án..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkApproveCancel} disabled={isBulkProcessing}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkApproveConfirm}
            variant="contained"
            color="success"
            disabled={isBulkProcessing}
            startIcon={isBulkProcessing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {isBulkProcessing ? 'Đang duyệt...' : 'Duyệt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reject Dialog */}
      <Dialog open={bulkRejectDialogOpen} onClose={handleBulkRejectCancel} maxWidth="md" fullWidth>
        <DialogTitle>Yêu cầu chỉnh sửa nhiều giáo án</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn đang yêu cầu chỉnh sửa {lessonPlans.filter(lp => selectedLessonPlanIds.includes(lp.id) && (lp.status === 'Pending' || lp.status === 'UnderReview')).length} giáo án
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Góp ý chỉnh sửa *"
            value={bulkRevisionNotes}
            onChange={(e) => setBulkRevisionNotes(e.target.value)}
            placeholder="Nhập góp ý chỉnh sửa cho tất cả giáo án..."
            required
            error={bulkRejectDialogOpen && !bulkRevisionNotes.trim()}
            helperText={bulkRejectDialogOpen && !bulkRevisionNotes.trim() ? 'Góp ý chỉnh sửa là bắt buộc' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkRejectCancel} disabled={isBulkProcessing}>
            Hủy
          </Button>
          <Button
            onClick={handleBulkRejectConfirm}
            variant="contained"
            color="error"
            disabled={isBulkProcessing || !bulkRevisionNotes.trim()}
            startIcon={isBulkProcessing ? <CircularProgress size={20} /> : <ReturnIcon />}
          >
            {isBulkProcessing ? 'Đang xử lý...' : 'Yêu cầu chỉnh sửa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer - Full Screen */}
      <LessonPlanFileViewer
        lessonPlan={lessonPlanForViewer}
        open={fileViewerOpen}
        onClose={() => {
          setFileViewerOpen(false);
          setLessonPlanForViewer(null);
        }}
        onDownload={handleDownloadLessonPlan}
        canApprove={showAdminActions}
        onApprove={handleApproveClick}
        onReview={handleReviewClick}
        onReject={handleRejectClick}
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </>
  );
};
